import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import type { LoaderFunctionArgs } from 'react-router-dom';
import type { IUnitCreate, IUnitUpdate } from './types';

import { unitService } from './service';
import { useNotification } from '@/hooks/useNotification';
import { getIdFromParams, getPageFromRequest, useIdFromParams, usePageFromSearchParams } from '@/utils/params';
import { routes } from '@/app/router/routes';

const unitsQuery = (page: number) => ({
  queryKey: ['units', 'list', page],
  queryFn: () => unitService.getUnits(page),
  staleTime: 1000 * 60 * 24,
});

export const unitsLoader =
  (queryClient: QueryClient) =>
  async ({ request }: LoaderFunctionArgs) => {
    const page = getPageFromRequest(request);
    const query = unitsQuery(page);
    return await queryClient.ensureQueryData(query);
  };

export type UnitsLoaderType = Awaited<ReturnType<ReturnType<typeof unitsLoader>>>;

export const useUnitsQuery = (initialData: UnitsLoaderType) => {
  const page = usePageFromSearchParams();
  const queryInfo = useQuery({
    ...unitsQuery(page),
    initialData,
  });

  return queryInfo;
};

const unitQuery = (id: number) => ({
  queryKey: ['units', 'details', id],
  queryFn: () => unitService.getUnit(id),
});

export const unitLoader =
  (queryClient: QueryClient) =>
  async ({ params }: LoaderFunctionArgs) => {
    const id = getIdFromParams(params);
    const query = unitQuery(id);
    return await queryClient.ensureQueryData(query);
  };

export type UnitLoaderType = Awaited<ReturnType<ReturnType<typeof unitLoader>>>;

export const useUnitQuery = (initialData: UnitLoaderType) => {
  const id = useIdFromParams();
  const queryInfo = useQuery({
    ...unitQuery(id),
    initialData,
  });

  return queryInfo;
};

export const useUnitCreate = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { successNotification } = useNotification();

  const mutationInfo = useMutation({
    mutationFn: (data: IUnitCreate) => unitService.createUnit(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['units', 'list'] });
      successNotification('units.notification.createMessage');
      navigate(routes.units());
    },
  });

  return mutationInfo;
};

export const useUnitUpdate = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { successNotification } = useNotification();

  const mutationInfo = useMutation({
    mutationFn: (data: IUnitUpdate) => unitService.updateUnit(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['units', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['units', 'details', variables.id] });
      successNotification('units.notification.updateMessage');
      navigate(routes.units());
    },
  });

  return mutationInfo;
};

export const useUnitDelete = () => {
  const queryClient = useQueryClient();
  const { successNotification } = useNotification();

  const mutationInfo = useMutation({
    mutationFn: (id: number) => unitService.deleteUnit(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['units', 'list'] });
      successNotification('units.notification.deleteMessage');
    },
  });

  return mutationInfo;
};
